/** Fields needed to restore box assignments; omit images/descriptions to keep drafts small. */
const BOX_ITEM_FIELDS = [
  'id',
  'title',
  'sku',
  'quantity',
  'variant_title',
  'variant_weight',
  'product_weight',
  'weight_oz',
  'resolved_weight',
  'product_variant_id',
  'selected',
] as const;

const RATE_FIELDS = [
  'rate_id',
  'carrier',
  'service_name',
  'service_code',
  'total_cost',
  'cost',
  'delivery_days',
  'estimated_delivery_date',
  'boxes',
  'confidence',
  'message',
] as const;

const LABEL_FIELDS = [
  'tracking',
  'carrier',
  'service',
  'cost',
  'status',
  'box_number',
  'label_format',
  'customs_form_url',
  'is_international',
  'shipment_id',
] as const;

function pickFields<T extends Record<string, unknown>>(obj: T, fields: readonly string[]): Partial<T> {
  const result: Partial<T> = {};
  for (const key of fields) {
    if (obj[key] !== undefined) {
      result[key as keyof T] = obj[key as keyof T];
    }
  }
  return result;
}

function slimBoxItem(item: Record<string, unknown>) {
  return pickFields(item, BOX_ITEM_FIELDS);
}

function slimBox(box: Record<string, unknown>) {
  return {
    id: box.id,
    items: Array.isArray(box.items) ? box.items.map((item) => slimBoxItem(item as Record<string, unknown>)) : [],
    weight: box.weight,
    weightLb: box.weightLb,
    weightOz: box.weightOz,
    length: box.length,
    width: box.width,
    height: box.height,
    packagingWeight: box.packagingWeight,
  };
}

function slimRate(rate: Record<string, unknown> | null | undefined) {
  if (!rate) return rate;
  return pickFields(rate, RATE_FIELDS);
}

/** Never persist base64 label PDFs — they can be several MB each. */
function slimLabel(label: Record<string, unknown>) {
  return pickFields(label, LABEL_FIELDS);
}

/** Draft data round-trips through JSON.parse, so there are no compile-time schema guarantees. */
export type ShippingDraftData = Record<string, any>;

export function slimShippingDraftData(data: ShippingDraftData): ShippingDraftData {
  return {
    ...data,
    boxes: Array.isArray(data.boxes)
      ? data.boxes.map((box) => slimBox(box as Record<string, unknown>))
      : data.boxes,
    availableRates: Array.isArray(data.availableRates)
      ? data.availableRates.map((rate) => slimRate(rate as Record<string, unknown>))
      : data.availableRates,
    selectedRate: slimRate(data.selectedRate as Record<string, unknown>),
    labels: Array.isArray(data.labels)
      ? data.labels.map((label) => slimLabel(label as Record<string, unknown>))
      : data.labels,
    items: Array.isArray(data.items)
      ? data.items.map((item) => slimBoxItem(item as Record<string, unknown>))
      : data.items,
  };
}

export function cleanupOldShippingDrafts(keepKey?: string) {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || key === keepKey) continue;
    if (key.startsWith('shipping_draft_') || key.startsWith('unified_shipping_draft_')) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

export function loadShippingDraft(key: string): ShippingDraftData | null {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

export function saveShippingDraft(key: string, data: ShippingDraftData): boolean {
  const slimmed = slimShippingDraftData(data);

  const trySave = (payload: ShippingDraftData) => {
    localStorage.setItem(key, JSON.stringify(payload));
  };

  try {
    trySave(slimmed);
    return true;
  } catch (err) {
    const isQuotaError =
      err instanceof DOMException &&
      (err.name === 'QuotaExceededError' || err.code === 22);

    if (!isQuotaError) {
      console.warn('[ShippingDraft] Failed to save draft:', err);
      return false;
    }

    console.warn('[ShippingDraft] Storage quota exceeded — clearing old drafts and retrying');
    cleanupOldShippingDrafts(key);

    try {
      trySave(slimmed);
      return true;
    } catch {
      // Drop rates (can be re-fetched) but keep box layout and step
      try {
        trySave({
          ...slimmed,
          availableRates: [],
          selectedRate: slimmed.selectedRate ?? null,
        });
        return true;
      } catch {
        try {
          trySave({
            step: slimmed.step,
            boxes: slimmed.boxes,
            items: slimmed.items,
            insuranceOption: slimmed.insuranceOption,
            insuranceAmount: slimmed.insuranceAmount,
            ratesInsuranceAmount: slimmed.ratesInsuranceAmount,
            ratesInsuranceProvider: slimmed.ratesInsuranceProvider,
            signatureOption: slimmed.signatureOption,
            selectedLocationId: slimmed.selectedLocationId,
            methodMatch: slimmed.methodMatch,
          });
          return true;
        } catch (finalErr) {
          console.warn('[ShippingDraft] Could not save draft after cleanup:', finalErr);
          return false;
        }
      }
    }
  }
}

export function removeShippingDraft(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}
